import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  Award,
  CheckCircle2,
  Clock,
  AlertCircle,
  ArrowLeft,
  FileText,
  Calendar,
} from 'lucide-react'
import {
  getPilotProjects,
  getPilotMilestones,
  getPilotEvidence,
} from '../../services/api'
import { format, parseISO } from 'date-fns'
import clsx from 'clsx'

const STATUS_STYLES = {
  pending: 'bg-gray-100 text-gray-700',
  in_progress: 'bg-blue-100 text-blue-800',
  evidence_submitted: 'bg-amber-100 text-amber-800',
  verified: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
}

const STATUS_ICONS = {
  pending: Clock,
  in_progress: Clock,
  evidence_submitted: FileText,
  verified: CheckCircle2,
  failed: AlertCircle,
}

function StatusBadge({ status }) {
  const Icon = STATUS_ICONS[status] || Clock
  const cls = STATUS_STYLES[status] || STATUS_STYLES.pending
  return (
    <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium', cls)}>
      <Icon className="h-3 w-3" />
      {status.replace(/_/g, ' ')}
    </span>
  )
}

function MilestoneCard({ milestone }) {
  const [showEvidence, setShowEvidence] = useState(false)
  const { data: evidence, isLoading: loadingEvidence } = useQuery({
    queryKey: ['pilotEvidence', milestone.id],
    queryFn: () => getPilotEvidence(milestone.id),
    enabled: showEvidence,
  })

  const requiredCount = (milestone.evidence_required || []).filter(e => e.required).length
  const attachedCount = milestone.evidence_count || 0
  const overdue = milestone.due_date && new Date(milestone.due_date) < new Date() && milestone.status !== 'verified'

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-sm transition-shadow">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-xs text-gray-500">{milestone.code}</span>
            <StatusBadge status={milestone.status} />
            {overdue && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                <AlertCircle className="h-3 w-3" />
                overdue
              </span>
            )}
          </div>
          <h3 className="font-semibold text-gray-900">{milestone.title}</h3>
          {milestone.description && (
            <p className="text-sm text-gray-600 mt-1 line-clamp-2">{milestone.description}</p>
          )}
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
            {milestone.due_date && (
              <span className="inline-flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                Due {format(parseISO(milestone.due_date), 'd MMM yyyy')}
              </span>
            )}
            <span>
              Evidence: <span className="font-medium text-gray-700">{attachedCount}</span> attached / {requiredCount} required
            </span>
          </div>
        </div>
        <button
          onClick={() => setShowEvidence(!showEvidence)}
          className="text-xs text-green-600 hover:text-green-700 hover:underline whitespace-nowrap"
        >
          {showEvidence ? 'Hide' : 'View'} evidence
        </button>
      </div>

      {showEvidence && (
        <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
          <div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Required evidence</h4>
            <ul className="space-y-2">
              {(milestone.evidence_required || []).map((req, idx) => (
                <li key={idx} className="text-sm flex items-start gap-2">
                  <span className="font-mono text-xs text-gray-400 mt-0.5">{req.clause_ref || '—'}</span>
                  <div className="flex-1">
                    <span className="text-gray-700">{req.description || req.key}</span>
                    {req.kind && (
                      <span className="ml-2 text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
                        {req.kind}
                      </span>
                    )}
                    {req.thresholds && (
                      <div className="text-xs text-gray-500 mt-0.5">
                        Thresholds: {Object.entries(req.thresholds).map(([k, v]) => `${k}=${v}`).join(', ')}
                      </div>
                    )}
                    {req.source_table_hint && (
                      <div className="text-xs text-gray-400 mt-0.5">Source: <code>{req.source_table_hint}</code></div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Attached evidence</h4>
            {loadingEvidence && <p className="text-sm text-gray-500">Loading...</p>}
            {!loadingEvidence && evidence && evidence.length === 0 && (
              <p className="text-sm text-gray-500 italic">No evidence attached yet.</p>
            )}
            {!loadingEvidence && evidence && evidence.length > 0 && (
              <ul className="space-y-2">
                {evidence.map(link => (
                  <li key={link.id} className="text-sm bg-gray-50 rounded p-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-mono text-xs text-gray-500">{link.evidence_key}</span>
                        <span className="ml-2 text-gray-700"><code className="text-xs">{link.source_table}</code></span>
                      </div>
                      <span className="text-xs text-gray-400">
                        {link.attached_at && format(parseISO(link.attached_at), 'd MMM yyyy')}
                      </span>
                    </div>
                    {link.note && <p className="text-xs text-gray-600 mt-1">{link.note}</p>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function PilotCard({ pilot, onClick, isActive }) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'w-full text-left bg-white rounded-lg border p-4 transition-all',
        isActive ? 'border-green-500 shadow-md' : 'border-gray-200 hover:border-gray-300 hover:shadow-sm',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <Award className="h-5 w-5 text-green-600 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-gray-900 leading-snug">{pilot.title}</h3>
            <p className="text-xs text-gray-500 mt-0.5">{pilot.grantor}</p>
          </div>
        </div>
        <StatusBadge status={pilot.status} />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-600">
        {pilot.grant_ref && (
          <div>
            <span className="text-gray-400">Ref:</span> <span className="font-mono">{pilot.grant_ref}</span>
          </div>
        )}
        {pilot.grant_amount != null && (
          <div>
            <span className="text-gray-400">Grant:</span>{' '}
            <span className="font-medium">
              {pilot.currency} {Number(pilot.grant_amount).toLocaleString()}
            </span>
          </div>
        )}
        {pilot.start_date && (
          <div>
            <span className="text-gray-400">Start:</span> {format(parseISO(pilot.start_date), 'd MMM yyyy')}
          </div>
        )}
        {pilot.end_date && (
          <div>
            <span className="text-gray-400">End:</span> {format(parseISO(pilot.end_date), 'd MMM yyyy')}
          </div>
        )}
      </div>
    </button>
  )
}

export default function PilotTracker({ ranchId }) {
  const [activePilotId, setActivePilotId] = useState(null)

  const { data: pilots, isLoading: loadingPilots, error: pilotsError } = useQuery({
    queryKey: ['pilotProjects', ranchId],
    queryFn: () => getPilotProjects(ranchId),
    enabled: !!ranchId,
  })

  // Auto-select first pilot
  if (pilots && pilots.length > 0 && !activePilotId) {
    setActivePilotId(pilots[0].id)
  }

  const { data: milestones, isLoading: loadingMilestones } = useQuery({
    queryKey: ['pilotMilestones', activePilotId],
    queryFn: () => getPilotMilestones(activePilotId),
    enabled: !!activePilotId,
  })

  const activePilot = pilots?.find(p => p.id === activePilotId)
  const verifiedCount = milestones?.filter(m => m.status === 'verified').length || 0
  const totalCount = milestones?.length || 0
  const progress = totalCount > 0 ? Math.round((verifiedCount / totalCount) * 100) : 0

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/dashboard" className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="h-5 w-5 text-gray-500" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Award className="h-6 w-6 text-green-600" />
            Pilot Tracker
          </h1>
          <p className="text-gray-500 mt-1">
            Grant-funded pilots for this ranch. CAL §3.4 milestone evidence.
          </p>
        </div>
      </div>

      {pilotsError && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 flex items-start gap-2">
          <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Failed to load pilots</p>
            <p className="text-sm mt-1">{pilotsError.message}</p>
          </div>
        </div>
      )}

      {loadingPilots && (
        <p className="text-gray-500">Loading pilots...</p>
      )}

      {!loadingPilots && pilots && pilots.length === 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <Award className="h-10 w-10 text-gray-400 mx-auto mb-2" />
          <h3 className="font-semibold text-gray-900">No pilot projects</h3>
          <p className="text-sm text-gray-500 mt-1">
            This ranch has no grant-funded pilots registered. A system admin can register one.
          </p>
        </div>
      )}

      {!loadingPilots && pilots && pilots.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {pilots.map(pilot => (
              <PilotCard
                key={pilot.id}
                pilot={pilot}
                isActive={pilot.id === activePilotId}
                onClick={() => setActivePilotId(pilot.id)}
              />
            ))}
          </div>

          {activePilot && (
            <div className="space-y-4">
              <div className="flex items-center justify-between bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
                <div>
                  <h2 className="font-semibold text-gray-900">{activePilot.title}</h2>
                  {activePilot.notes && (
                    <p className="text-sm text-gray-600 mt-1">{activePilot.notes}</p>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-green-700">{progress}%</div>
                  <div className="text-xs text-gray-600">{verifiedCount} / {totalCount} verified</div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Milestones</h3>
                {loadingMilestones && <p className="text-gray-500">Loading milestones...</p>}
                {!loadingMilestones && milestones && (
                  <div className="space-y-3">
                    {milestones.map(m => (
                      <MilestoneCard key={m.id} milestone={m} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
